// ReceiptSummary.tsx
import React, { useState } from "react";
import "../App.scss";
import { Stack, FontIcon } from "@fluentui/react";
import SummaryTable from "./SummaryTable";
import ReceiptBasicInfo from "./ReceiptInfoBasic";
import { useNavigate } from "react-router";
import { IProductItem, IReport_ProductItem } from "../../../shared/interfaces/IReceipAnalyzerEntities";
import ActionButton from "./ActionButton";
import SConsciousApiService from "../services/SConsciousApiService";
import MerchantPicker from "./MerchantPicker";


interface ReceiptSummaryProps {
    analysisResult: any;
    userId?: string;
    onReanalyzed?: (result: any) => void;
    children?: React.ReactNode;
}

const ReceiptSummary: React.FC<ReceiptSummaryProps> = ({ analysisResult, userId, onReanalyzed, children }) => {
    const { receipt, merchant, items } = analysisResult;
    const navigate = useNavigate();
    const [reanalyzing, setReanalyzing] = useState(false);
    const [reanalyzeError, setReanalyzeError] = useState<string | null>(null);
    const [reanalyzeCount, setReanalyzeCount] = useState(0);
    const [userRemark, setUserRemark] = useState('');
    const [reanalyzeReward, setReanalyzeReward] = useState<string | null>(null);
    const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
    const [showMerchantPicker, setShowMerchantPicker] = useState(false);
    const [merchantRewardMessage, setMerchantRewardMessage] = useState<string | null>(null);
    const [localMerchant, setLocalMerchant] = useState<{ id: string; name: string } | null>(null);
    const MAX_REANALYZE = 2;
    const _api = new SConsciousApiService();

    const handleDeleteItem = async (itemId: string) => {
        if (!userId || !receipt?.receipt_id || !itemId) return;
        if (!window.confirm('Delete this item?')) return;
        setDeletingItemId(itemId);
        const result = await _api.deleteItem(itemId, receipt.receipt_id, userId);
        setDeletingItemId(null);
        if (result?.success) {
            if (onReanalyzed) {
                const updatedItems = (items || []).filter((i: any) => i.item_id !== itemId);
                onReanalyzed({ ...analysisResult, items: updatedItems });
            }
            window.location.reload();
        }
    };

    // Always compute from live items — pre-computed values on the receipt record may be stale after item deletion or reanalysis
    const itemsTotal = Array.isArray(items)
        ? Math.round(items.reduce((s: number, i: any) => s + (i.total_price ?? 0), 0) * 100) / 100
        : 0;
    const receiptTotal: number = receipt.total_amount ?? 0;
    const tolerance = Math.max(receiptTotal * 0.02, 0.50);
    const totalChecksOut: boolean = Math.abs(itemsTotal - receiptTotal) <= tolerance;
    const priceDifference: number = Math.round((receiptTotal - itemsTotal) * 100) / 100;

    // Rounding: tiny unexplained gap ≤ €0.05 — show as a line item, not a warning
    const isRounding = Math.abs(priceDifference) > 0.001 && Math.abs(priceDifference) <= 0.05;
    const displayItems = isRounding
        ? [...(items || []), { _isRounding: true, description: 'Rounding', quantity: null, total_price: priceDifference, currency_code: receipt.currency_code }]
        : (items || []);

    const handleReanalyze = async () => {
        if (!userId || !receipt?.receipt_id || reanalyzeCount >= MAX_REANALYZE) return;
        setReanalyzing(true);
        setReanalyzeError(null);
        const result = await _api.reanalyzeReceipt(receipt.receipt_id, userId, userRemark.trim() || undefined);
        setReanalyzing(false);
        setReanalyzeCount(c => c + 1);
        setUserRemark('');
        if (result?.error) {
            setReanalyzeError(result.error);
        } else {
            setReanalyzeReward('+0.5 sCoN earned for re-analyzing!');
            if (onReanalyzed) onReanalyzed(result);
        }
    };
    return (
        <>
            <Stack horizontalAlign="center" style={{ width: '100%', margin: '0 auto' }}>
                <div className="receipt-summary">
                    <br/>
                    <div style={{ background: 'rgba(0,0,0,0.08)', width: '90%', borderRadius: '8px', margin: '0 auto', padding: '18px 0' }}>

                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                            <ActionButton label={localMerchant?.name || merchant?.name || 'Unknown Merchant'} style={{ fontSize: 16, margin: 0 }} onClick={() => navigate(`/Merchant?merchantId=${localMerchant?.id || merchant?.merchant_id}`)} />
                            {userId && (
                                <button
                                    onClick={() => { setShowMerchantPicker(p => !p); setMerchantRewardMessage(null); }}
                                    style={{ fontFamily: 'inherit', background: showMerchantPicker ? '#000' : 'rgba(0,0,0,0.10)', color: showMerchantPicker ? '#ffcc00' : '#000', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                                    title="Change merchant"
                                >
                                    <FontIcon iconName="Edit" style={{ fontSize: '0.75rem' }} />
                                </button>
                            )}
                        </div>
                        {merchantRewardMessage && !showMerchantPicker && (
                            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#000', background: '#ffcc00', borderRadius: 8, padding: '6px 10px', margin: '6px auto 0', maxWidth: '80%' }}>
                                {merchantRewardMessage}
                            </div>
                        )}
                        {showMerchantPicker && userId && receipt?.receipt_id && (
                            <div style={{ width: '90%', maxWidth: 340, margin: '8px auto 0' }}>
                                <MerchantPicker
                                    receiptId={receipt.receipt_id}
                                    userId={userId}
                                    currentMerchantName={localMerchant?.name || merchant?.name || 'Unknown'}
                                    onSaved={(merchantId, merchantName) => {
                                        setLocalMerchant({ id: merchantId, name: merchantName });
                                        setShowMerchantPicker(false);
                                        setMerchantRewardMessage('+1 sCoN earned for updating merchant!');
                                        if (onReanalyzed) {
                                            onReanalyzed({ ...analysisResult, merchant: { ...merchant, merchant_id: merchantId, name: merchantName } });
                                        }
                                    }}
                                    onCancel={() => setShowMerchantPicker(false)}
                                />
                            </div>
                        )}
                        <br/>
                        <br/>
                        <br/>
                        <div style={{ marginBottom: 8, fontSize: 12 }}>
                            <strong>Date:</strong> {receipt.transaction_date} &nbsp; <strong>Time:</strong> {receipt.transaction_time}
                        </div>
                        <div style={{ marginBottom: 8, fontSize: 12 }}>
                            <strong>Total:</strong> {receipt.total_amount?.toFixed(2)} {receipt.currency_code}
                            {receipt.home_currency_code && receipt.home_currency_code !== receipt.currency_code && (
                                <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.75 }}>
                                    ≈ {receipt.home_total_amount?.toFixed(2)} {receipt.home_currency_code}
                                    <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.6 }}>@ {receipt.exchange_rate?.toFixed(4)}</span>
                                </span>
                            )}
                        </div>
                        {/* Participant receipt: copied from original */}
                        {receipt.source_receipt_id && (
                            <div
                                onClick={() => {
                                    if (receipt.split_id) navigate(`/BillSplit?splitId=${encodeURIComponent(receipt.split_id)}`);
                                }}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    background: '#000', color: '#ffcc00', borderRadius: 20,
                                    padding: '4px 12px', fontSize: '0.75rem', fontWeight: 700,
                                    marginBottom: 8,
                                    cursor: receipt.split_id ? 'pointer' : 'default',
                                }}>
                                <FontIcon iconName="People" style={{ fontSize: 12 }} />
                                Split Bill — Your share
                            </div>
                        )}
                        {/* Owner receipt — split active (not yet confirmed) */}
                        {receipt.split_id && !receipt.source_receipt_id && !receipt.split_deduction && (
                            <div
                                onClick={() => navigate(`/BillSplit?splitId=${encodeURIComponent(receipt.split_id!)}`)}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    background: 'rgba(0,0,0,0.08)', color: '#000', borderRadius: 20,
                                    padding: '4px 12px', fontSize: '0.75rem', fontWeight: 700,
                                    marginBottom: 8, cursor: 'pointer',
                                }}>
                                <FontIcon iconName="People" style={{ fontSize: 12 }} />
                                Split in progress
                            </div>
                        )}
                        {/* Owner receipt — split confirmed, total reduced */}
                        {receipt.split_id && !receipt.source_receipt_id && receipt.split_deduction != null && receipt.split_deduction > 0 && (
                            <div
                                onClick={() => navigate(`/BillSplit?splitId=${encodeURIComponent(receipt.split_id!)}`)}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    background: '#000', color: '#ffcc00', borderRadius: 20,
                                    padding: '4px 12px', fontSize: '0.75rem', fontWeight: 700,
                                    marginBottom: 8, cursor: 'pointer',
                                }}>
                                <FontIcon iconName="People" style={{ fontSize: 12 }} />
                                Bill Split — -{receipt.split_deduction.toFixed(2)} {receipt.currency_code} split off
                            </div>
                        )}
                        <div style={{ marginBottom: 8, fontSize: 12 }}>
                            <strong>Type:</strong> {receipt.receipt_type}
                        </div>
                        {receipt.tokens_used != null && receipt.tokens_used > 0 && (
                            <div style={{ marginBottom: 8, fontSize: 12, opacity: 0.6 }}>
                                <strong>AI tokens:</strong> {receipt.tokens_used.toLocaleString()}
                            </div>
                        )}
                    </div>
                    {/* Reconciliation failed — all auto-retries exhausted, ask user to scan again */}
                    {receipt.reconciliation_failed && !receipt.source_receipt_id && (
                        <div style={{
                            width: '90%', margin: '12px auto 0',
                            background: 'rgba(0,0,0,0.08)', borderRadius: 8
                        }}>
                            <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#000', marginBottom: 8 }}>
                                ✗ Could Not Reconcile Receipt
                            </div>
                            <div style={{ fontSize: '0.82rem', color: '#000', lineHeight: 1.6, marginBottom: 12 }}>
                                We tried 2 times with our vision model but could not reconcile the item totals with the receipt total.
                                The receipt data below may be incomplete.<br /><br />
                                <strong>Please delete this receipt and scan it again</strong>, ideally with better lighting and a flat surface.
                            </div>
                            <div style={{ fontSize: 12, lineHeight: 1.5, opacity: 0.5 }}>
                                Items total: {itemsTotal.toFixed(2)} {receipt.currency_code}&nbsp;&nbsp;·&nbsp;&nbsp;
                                Receipt total: {receiptTotal.toFixed(2)} {receipt.currency_code}&nbsp;&nbsp;·&nbsp;&nbsp;
                                Difference: {priceDifference.toFixed(2)} {receipt.currency_code}
                            </div>
                            {userId && (
                                <div style={{ marginTop: 10 }}>
                                    {reanalyzeCount < MAX_REANALYZE ? (
                                        <>
                                            <input
                                                className="darkInput"
                                                placeholder="Optional: tell the model what to fix…"
                                                value={userRemark}
                                                onChange={e => setUserRemark(e.target.value)}
                                                maxLength={300}
                                                disabled={reanalyzing}
                                                style={{ fontFamily: 'inherit', width: '90%', marginBottom: 8, fontSize: '0.82rem' }}
                                            />
                                            <button
                                                className="actionButton"
                                                onClick={handleReanalyze}
                                                disabled={reanalyzing}
                                                style={{margin: '10px auto', fontFamily: 'inherit', fontSize: '0.82rem', opacity: reanalyzing ? 0.6 : 1 }}
                                            >
                                                {reanalyzing ? 'Re-analyzing…' : `Re-analyze${reanalyzeCount > 0 ? ` (${MAX_REANALYZE - reanalyzeCount} left)` : ''}`}
                                            </button>
                                            {reanalyzeError && <span style={{ marginLeft: 12, fontSize: '0.78rem', color: '#ff4444' }}>{reanalyzeError}</span>}
                                            {reanalyzeReward && <div style={{ fontSize: '0.78rem', color: '#000', fontWeight: 700, marginTop: 6 }}>{reanalyzeReward}</div>}
                                        </>
                                    ) : (
                                        <span style={{ fontSize: '0.78rem', opacity: 0.5 }}>Re-analysis limit reached. Please delete and re-scan this receipt.</span>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {/* Price mismatch warning — shown only when totals genuinely don't check out (and not already flagged as failed) */}
                    {/* Bill split deduction explains the discrepancy — show as a split note, not a generic warning */}
                    {receipt.split_id && !receipt.source_receipt_id && receipt.split_deduction != null && receipt.split_deduction > 0 && Math.abs(priceDifference + receipt.split_deduction) <= 0.05 && !receipt.reconciliation_failed && (
                        <div style={{ width: '90%', margin: '12px auto 0', background: 'rgba(0,0,0,0.08)', borderRadius: 8, padding: '10px 14px' }}>
                            <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>🔀 Bill Split Applied</div>
                            <div style={{ fontSize: '0.82rem', opacity: 0.7, lineHeight: 1.5 }}>
                                Items totalled {(receipt.total_amount + receipt.split_deduction).toFixed(2)} {receipt.currency_code}. {receipt.split_deduction.toFixed(2)} {receipt.currency_code} was split with others, leaving your share of {receipt.total_amount?.toFixed(2)} {receipt.currency_code}.
                            </div>
                        </div>
                    )}
                    {!receipt.reconciliation_failed && !isRounding && !receipt.source_receipt_id && !(receipt.split_deduction != null && receipt.split_deduction > 0 && Math.abs(priceDifference + receipt.split_deduction) <= 0.05) && (!totalChecksOut || (receipt.explanationNotes && Math.abs(priceDifference) > 0.005)) && (
                        <div style={{
                            width: '90%', margin: '12px auto 0',
                            background: 'rgba(0,0,0,0.08)', borderRadius: 8
                        }}>
                            <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#000', marginBottom: 6 }}>
                                {priceDifference > 0 ? '⚠ Potential Overcharge' : '⚠ Unaccounted Discount'}
                            </div>
                            {Math.abs(priceDifference) > 0 && (
                                <div style={{ fontSize: 12, marginBottom: 6 }}>
                                    <span>Items total:&nbsp;<strong>{itemsTotal.toFixed(2)} {receipt.currency_code}</strong></span>
                                    <span style={{ margin: '0 10px', opacity: 0.5 }}>·</span>
                                    <span>Receipt total:&nbsp;<strong>{receiptTotal.toFixed(2)} {receipt.currency_code}</strong></span>
                                    <span style={{ margin: '0 10px', opacity: 0.5 }}>·</span>
                                    <span style={{ fontWeight: 700 }}>
                                        {priceDifference > 0 ? '+' : ''}{priceDifference.toFixed(2)} {receipt.currency_code}
                                    </span>
                                </div>
                            )}
                            <div style={{ fontSize: '0.82rem', opacity: 0.6, lineHeight: 1.5 }}>
                                {receipt.explanationNotes || (
                                    priceDifference < 0
                                        ? `The itemised lines sum to ${itemsTotal.toFixed(2)} ${receipt.currency_code} but the receipt total is ${receiptTotal.toFixed(2)} ${receipt.currency_code}. A discount or reduction of ${Math.abs(priceDifference).toFixed(2)} ${receipt.currency_code} is not captured as a line item. Re-analyze to fix this.`
                                        : `The store may have charged ${priceDifference.toFixed(2)} ${receipt.currency_code} more than the itemised lines. Re-analyze to investigate.`
                                )}
                            </div>
                            {userId && (
                                <div style={{ marginTop: 10 }}>
                                    {reanalyzeCount < MAX_REANALYZE ? (
                                        <>
                                            <input
                                                className="darkInput"
                                                placeholder="Optional: tell the model what to fix…"
                                                value={userRemark}
                                                onChange={e => setUserRemark(e.target.value)}
                                                maxLength={300}
                                                disabled={reanalyzing}
                                                style={{ fontFamily: 'inherit', width: '90%', marginBottom: 8, fontSize: '0.82rem' }}
                                            />
                                            <button
                                                className="actionButton"
                                                onClick={handleReanalyze}
                                                disabled={reanalyzing}
                                                style={{margin: '10px auto', fontFamily: 'inherit', fontSize: '0.82rem', opacity: reanalyzing ? 0.6 : 1 }}
                                            >
                                                {reanalyzing ? 'Re-analyzing…' : `Re-analyze${reanalyzeCount > 0 ? ` (${MAX_REANALYZE - reanalyzeCount} left)` : ''}`}
                                            </button>
                                            {reanalyzeError && <span style={{ marginLeft: 12, fontSize: '0.78rem', color: '#ff4444' }}>{reanalyzeError}</span>}
                                            {reanalyzeReward && <div style={{ fontSize: '0.78rem', color: '#000', fontWeight: 700, marginTop: 6 }}>{reanalyzeReward}</div>}
                                        </>
                                    ) : (
                                        <span style={{ fontSize: '0.78rem', opacity: 0.5 }}>Re-analysis limit reached. Please delete and re-scan this receipt.</span>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    <br/>
                    <div>
                        <strong style={{ fontSize: 14}}>Receipt Products:</strong>

                        <Stack horizontalAlign="center" style={{ margin: '10px Auto', width: '90%' }}>
                            <SummaryTable
                                data={(displayItems).map((item: any, idx: any) => ({ id: idx, ...item }))}
                                columns={[
                                    { key: "description", label: "Description", width: userId ? "44%" : "48%", total: false, 
                                        render: (_: any, item: IProductItem & { _isRounding?: boolean }) => (
                                            item._isRounding
                                                ? <span className='table-cell-content' style={{ fontStyle: 'italic', opacity: 0.65 }}>Rounding</span>
                                                : <span className='table-cell-content' onClick={() => navigate(`/ProductItem?receiptId=${encodeURIComponent(item.receipt_id)}&itemId=${encodeURIComponent(item.item_id)}`)} style={{ cursor: 'pointer' }}>{item.description}</span>
                                        ),
                                    },
                                    { key: "quantity", label: "Qty", width: "14%", total: false },
                                    { key: "total_price", label: "Price", width: userId ? "30%" : "38%", total: true,
                                        render: (_: any, item: any) => (
                                            <span className='table-cell-content' style={item._isRounding ? { fontStyle: 'italic', opacity: 0.65 } : undefined}>
                                                {item.total_price?.toFixed(2)}
                                                {!item._isRounding && item.home_total_price != null && item.home_currency_code && item.home_currency_code !== item.currency_code && (
                                                    <span style={{ display: 'block', fontSize: 11, opacity: 0.65 }}>
                                                        ≈ {item.home_total_price.toFixed(2)} {item.home_currency_code}
                                                    </span>
                                                )}
                                            </span>
                                        ),
                                    },
                                    ...(userId ? [{ key: "item_id" as any, label: "", width: "12%", total: false,
                                        render: (_: any, item: any) => (
                                            item._isRounding ? null : (
                                                <span
                                                    className='table-cell-content'
                                                    onClick={() => handleDeleteItem(item.item_id)}
                                                    style={{ cursor: 'pointer', opacity: deletingItemId === item.item_id ? 0.3 : 0.4, fontSize: '0.82rem' }}
                                                    title="Delete item"
                                                >
                                                    ✕
                                                </span>
                                            )
                                        ),
                                    }] : []),
                                ] as any}
                                filterKeys={[]}
                                header={true}
                                total={true}    
                            />
                        </Stack>
                    </div>
                    {/* Bill Split */}
                    {userId && Array.isArray(items) && items.length > 0 && !receipt.source_receipt_id && (
                        <div style={{ width: '90%', margin: '0 auto' }}>
                            <button
                                className="actionButton"
                                onClick={() => navigate(`/BillSplit?receiptId=${encodeURIComponent(receipt.receipt_id)}`, {
                                    state: {
                                        items: items.filter((it: any) => !it._isRounding),
                                        totalAmount: receipt.total_amount,
                                        currencyCode: receipt.currency_code,
                                    },
                                })}
                                style={{ fontFamily: 'inherit', width: '100%', fontSize: '0.85rem', justifyContent: 'center', marginTop: 4, boxSizing: 'border-box' }}
                            >
                                <FontIcon iconName="People" style={{ marginRight: 6 }} />
                                Split the Bill
                            </button>
                        </div>
                    )}
                    <br/>
                </div>
            </Stack>
        </> 
    );
};

export default ReceiptSummary;